import { useState, useCallback } from 'react';

export interface StatusStep {
  id: string;
  label: string;
  icon: string;
  status: 'pending' | 'active' | 'completed' | 'error';
  description?: string;
}

export interface StatusModalState {
  isVisible: boolean;
  currentStep: string;
  progress: number;
  steps: StatusStep[];
  error?: Error;
  orderNumber?: string;
}

interface StatusModalControls {
  showValidating: () => void;
  showProcessing: ( progress: number ) => void;
  showGenerating: () => void;
  showCompleted: ( orderNumber: string ) => void;
  showError: ( error: Error, step: string ) => void;
  isError: boolean;
  setError: ( value: boolean ) => void;
  hide: () => void;
  retry: () => void;
  downloadPDF: () => void;
  newOrder: () => void;
}

const initialSteps: StatusStep[] = [
  {
    id: 'validating',
    label: 'Validando',
    icon: '🔍',
    status: 'pending',
    description: 'Verificando dados do pedido'
  },
  {
    id: 'processing',
    label: 'Processando',
    icon: '⚙️',
    status: 'pending',
    description: 'Criando pedido no sistema'
  },
  {
    id: 'generating',
    label: 'Gerando PDF',
    icon: '📄',
    status: 'pending',
    description: 'Preparando documento'
  },
  {
    id: 'completed',
    label: 'Concluído',
    icon: '✅',
    status: 'pending',
    description: 'Pedido finalizado com sucesso'
  }
];

export const useStatusModal = (): [ StatusModalState, StatusModalControls ] => {
  const [ error, setError ] = useState( false );
  const [ state, setState ] = useState<StatusModalState>( {
    isVisible: false,
    currentStep: '',
    progress: 0,
    steps: initialSteps,
    error: undefined,
    orderNumber: undefined,
  } );

  const updateStepStatus = useCallback( ( stepId: string, status: StatusStep[ 'status' ] ) => {
    setState( prev => ( {
      ...prev,
      steps: prev.steps.map( step =>
        step.id === stepId ? { ...step, status } : step
      )
    } ) );
  }, [] );

  const showValidating = useCallback( () => {
    setState( {
      isVisible: true,
      currentStep: 'validating',
      progress: 0,
      steps: initialSteps.map( step => ( {
        ...step,
        status: step.id === 'validating' ? 'active' : 'pending'
      } ) ),
      error: undefined,
      orderNumber: undefined,
    } );
  }, [] );

  const showProcessing = useCallback( ( progress: number ) => {
    setState( prev => ( {
      ...prev,
      currentStep: 'processing',
      progress,
      steps: prev.steps.map( step => ( {
        ...step,
        status: step.id === 'validating' ? 'completed' :
          step.id === 'processing' ? 'active' : 'pending'
      } ) )
    } ) );
  }, [] );

  const showGenerating = useCallback( () => {
    setState( prev => ( {
      ...prev,
      currentStep: 'generating',
      progress: 85,
      steps: prev.steps.map( step => ( {
        ...step,
        status: step.id === 'validating' || step.id === 'processing' ? 'completed' :
          step.id === 'generating' ? 'active' : 'pending'
      } ) )
    } ) );
  }, [] );

  const showCompleted = useCallback( ( orderNumber: string ) => {
    setState( prev => ( {
      ...prev,
      currentStep: 'completed',
      progress: 100,
      orderNumber,
      steps: prev.steps.map( step => ( { ...step, status: 'completed' } ) )
    } ) );
  }, [] );

  const showError = useCallback( ( error: Error, step: string ) => {
    setState( prev => ( {
      ...prev,
      currentStep: step,
      error,
      steps: prev.steps.map( s => ( {
        ...s,
        status: s.id === step ? 'error' :
          s.id === 'validating' && ( step === 'processing' || step === 'generating' ) ? 'completed' :
            s.id === 'processing' && step === 'generating' ? 'completed' :
              s.status
      } ) )
    } ) );
  }, [] );

  const hide = useCallback( () => {
    setState( prev => ( {
      ...prev,
      isVisible: false,
      currentStep: '',
      progress: 0,
      error: undefined,
      orderNumber: undefined,
    } ) );
  }, [] );

  const retry = useCallback( () => {
    // Reset to validating state
    showValidating();
  }, [ showValidating ] );

  const downloadPDF = useCallback( () => {
    // This will be handled by the parent component
    console.log( 'Download PDF action triggered' );
  }, [] );

  const newOrder = useCallback( () => {
    hide();
    // Additional logic to reset order state will be handled by parent
  }, [ hide ] );

  const controls: StatusModalControls = {
    showValidating,
    showProcessing,
    showGenerating,
    showCompleted,
    showError,
    isError: error,
    setError,
    hide,
    retry,
    downloadPDF,
    newOrder,
  };

  return [ state, controls ];
};